/**
 * Words and tones for the top bar's Rithmic account control (owner 2026-09-30: "the rithmic
 * account should be there on the top left like tradesea"; the prop / test indicators must not be
 * confused with the Rithmic account setup). Pure, so the header's labels are unit-tested.
 *
 * Two different facts are shown, and never mixed up:
 * - the **account's** environment (from its `AccountUpdate`): LIVE / PROP / SIM tag in the
 *   account control, also per account in its menu;
 * - the **login's** order plant (from `SessionState` / `LoginStatus`): which Rithmic system the
 *   account trades through and whether that connection is up, on the control's first line. The
 *   trading state ("Allow trading", reduce-only) stays the `SimBadge` next to it.
 */
import { LOGIN_STATE } from "./login.ts";
import { SESSION_STATUS, TRADING_ENV } from "./gate.ts";

/** The account-selector groups (web `AccountEnvironment`). */
export type AccountEnvKind = "live" | "demo" | "simulation";

/** live: real money (red) · prop: paper / prop-firm on a real Rithmic system (violet) · sim: mock
 * or Rithmic Test (amber) · off: no account. */
export type HeaderTone = "live" | "prop" | "sim" | "off";

export type AccountEnvTag = { tag: string; tone: HeaderTone; title: string };

/** The tag an account carries in the top-left control and in its menu. */
export function accountEnvTag(env: AccountEnvKind | null): AccountEnvTag {
  switch (env) {
    case "live":
      return { tag: "LIVE", tone: "live", title: "LIVE account — real money" };
    case "demo":
      return {
        tag: "PROP",
        tone: "prop",
        title:
          "Paper / prop-firm account (evaluation or funded) on a real Rithmic system — not your own money",
      };
    case "simulation":
      return {
        tag: "SIM",
        tone: "sim",
        title: "Simulated account (local mock or Rithmic Test) — no real orders",
      };
    default:
      return {
        tag: "NONE",
        tone: "off",
        title: "No account: none selected, or the Rithmic login has not reported one yet",
      };
  }
}

export type LoginLineInput = {
  login: { state: number; environment: number; systemName: string; detail: string } | null;
  session: { status: number; environment: number; systemName?: string } | null;
};

export type LoginLine = {
  /** "Rithmic Test", "Apex", "mock"… */
  system: string;
  state: "connected" | "connecting" | "error" | "off";
  /** "Connected", "Connecting…", "Rejected", "Not connected". */
  stateText: string;
};

function systemOf(env: number | undefined, name: string | undefined): string {
  if (name) return name;
  switch (env) {
    case TRADING_ENV.MOCK:
      return "mock";
    case TRADING_ENV.TEST:
      return "Rithmic Test";
    case TRADING_ENV.PAPER:
      return "Paper / prop";
    case TRADING_ENV.LIVE:
      return "LIVE";
    default:
      return "Rithmic";
  }
}

/** The control's first line: which Rithmic system, and is the login connected. */
export function rithmicLoginLine({ login, session }: LoginLineInput): LoginLine {
  const system = systemOf(
    login && login.environment !== TRADING_ENV.UNSPECIFIED ? login.environment : session?.environment,
    login?.systemName || session?.systemName,
  );
  if (login !== null) {
    switch (login.state) {
      case LOGIN_STATE.DISCONNECTED:
        // An explicit logout wins over the order plant's retained session state.
        return { system, state: "off", stateText: "Not connected" };
      case LOGIN_STATE.CONNECTED:
        return { system, state: "connected", stateText: "Connected" };
      case LOGIN_STATE.CONNECTING:
        return { system, state: "connecting", stateText: "Connecting…" };
      case LOGIN_STATE.REJECTED:
        return { system, state: "error", stateText: "Rejected" };
      case LOGIN_STATE.KICKED:
        return { system, state: "error", stateText: "Logged out" };
      default:
        break;
    }
  }
  // No login of the user's own (the dev mock / a service login): the order plant's state.
  if (session !== null) {
    if (session.status === SESSION_STATUS.READY)
      return { system, state: "connected", stateText: "Connected" };
    if (session.status === SESSION_STATUS.CONNECTING || session.status === SESSION_STATUS.SYNCING)
      return { system, state: "connecting", stateText: "Connecting…" };
    if (session.status === SESSION_STATUS.DEGRADED) return { system, state: "error", stateText: "Degraded" };
  }
  return {
    system: login === null && session === null ? "Rithmic" : system,
    state: "off",
    stateText: "Not connected",
  };
}

/**
 * Does the header show the LIVE stripe? When the selected account is live, or the order plant it
 * trades through is a LIVE system, so real money is never one glance away from looking like a test.
 */
export function isLiveHeader(sessionEnv: number | null | undefined, account: AccountEnvKind | null): boolean {
  return sessionEnv === TRADING_ENV.LIVE || account === "live";
}
